import React, { useEffect, useMemo, useState } from 'react';
import {
  Megaphone, Plus, Pencil, Trash2, Loader2, Save, X, Download, Calendar,
  Users, TrendingUp, Target, DollarSign, Radio, Newspaper, MonitorPlay, MapPin,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const PARTNER_TYPES = [
  { value: 'influencer', label: 'Influenceur', icon: Users },
  { value: 'billboard', label: 'Panneau d\'affichage', icon: MapPin },
  { value: 'radio_tv', label: 'Radio / TV', icon: Radio },
  { value: 'newspaper', label: 'Presse écrite', icon: Newspaper },
  { value: 'other', label: 'Autre', icon: MonitorPlay },
];

const STATUS_STYLES = {
  draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  active: { label: 'Actif', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  completed: { label: 'Terminé', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  cancelled: { label: 'Annulé', className: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const emptyForm = () => ({
  id: null,
  partner_name: '',
  partner_type: 'influencer',
  partner_phone: '',
  partner_email: '',
  period_start: '',
  period_end: '',
  total_amount: '',
  currency: 'XOF',
  description: '',
  deliverables: [],
  kpis: { target_views: 0, target_clicks: 0, target_leads: 0, actual_views: 0, actual_clicks: 0, actual_leads: 0 },
  payment_type: 'fixed',
  payment_schedule: [],
  commission_rate: 0,
  status: 'draft',
});

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const MarketingContracts = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get('/marketing-contracts'),
        api.get('/marketing-contracts/stats'),
      ]);
      setItems(l.data || []);
      setStats(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin]);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(i => i.status === filter || i.partner_type === filter);
  }, [items, filter]);

  const openCreate = () => setEditing(emptyForm());
  const openEdit = (c) => setEditing({
    ...emptyForm(),
    ...c,
    deliverables: c.deliverables || [],
    payment_schedule: c.payment_schedule || [],
    kpis: { ...emptyForm().kpis, ...(c.kpis || {}) },
  });
  const cancelEdit = () => setEditing(null);

  const addDeliverable = () => setEditing(p => ({ ...p, deliverables: [...p.deliverables, { platform: '', label: '', quantity: 1, expected_reach: null, notes: '' }] }));
  const updateDeliverable = (i, patch) => setEditing(p => ({ ...p, deliverables: p.deliverables.map((d, idx) => idx === i ? { ...d, ...patch } : d) }));
  const removeDeliverable = (i) => setEditing(p => ({ ...p, deliverables: p.deliverables.filter((_, idx) => idx !== i) }));

  const addInstallment = () => setEditing(p => ({ ...p, payment_schedule: [...p.payment_schedule, { label: '', amount: 0, due_date: '', paid_at: null }] }));
  const updateInstallment = (i, patch) => setEditing(p => ({ ...p, payment_schedule: p.payment_schedule.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const removeInstallment = (i) => setEditing(p => ({ ...p, payment_schedule: p.payment_schedule.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!editing.partner_name.trim()) return toast.error('Nom du partenaire requis');
    if (!editing.period_start || !editing.period_end) return toast.error('Période requise');
    setSaving(true);
    try {
      const payload = {
        ...editing,
        total_amount: parseFloat(editing.total_amount) || 0,
        commission_rate: parseFloat(editing.commission_rate) || 0,
        kpis: {
          target_views: parseInt(editing.kpis.target_views) || 0,
          target_clicks: parseInt(editing.kpis.target_clicks) || 0,
          target_leads: parseInt(editing.kpis.target_leads) || 0,
          actual_views: parseInt(editing.kpis.actual_views) || 0,
          actual_clicks: parseInt(editing.kpis.actual_clicks) || 0,
          actual_leads: parseInt(editing.kpis.actual_leads) || 0,
        },
        deliverables: editing.deliverables.filter(d => d.label && d.label.trim()),
        payment_schedule: editing.payment_schedule
          .filter(s => s.label && s.label.trim())
          .map(s => ({ ...s, amount: parseFloat(s.amount) || 0 })),
      };
      delete payload.id;
      delete payload.contract_number;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.created_by;
      delete payload.created_by_name;
      if (editing.id) {
        await api.put(`/marketing-contracts/${editing.id}`, payload);
        toast.success('Contrat mis à jour');
      } else {
        await api.post('/marketing-contracts', payload);
        toast.success('Contrat créé');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await api.delete(`/marketing-contracts/${confirmDelete.id}`);
      toast.success('Contrat supprimé');
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusyId(null); }
  };

  const doDownload = async (c) => {
    setBusyId(c.id);
    try {
      const r = await api.get(`/marketing-contracts/${c.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `contrat_pub_${c.contract_number}.pdf`;
      document.body.appendChild(link);
      link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('PDF téléchargé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur PDF'); }
    finally { setBusyId(null); }
  };

  const markPaid = async (c, idx) => {
    setBusyId(c.id);
    try {
      await api.post(`/marketing-contracts/${c.id}/mark-installment-paid?index=${idx}`);
      toast.success('Échéance marquée payée');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusyId(null); }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center">
        <Megaphone className="w-14 h-14 mx-auto text-gray-300 mb-3" />
        <h2 className="text-xl font-bold text-[#1B3A4B]">Module Marketing</h2>
        <p className="text-gray-500 mt-2">Ce module est réservé au super-administrateur.</p>
      </div>
    );
  }
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  const typeIcon = (t) => (PARTNER_TYPES.find(x => x.value === t)?.icon || Users);

  return (
    <div className="space-y-6" data-testid="marketing-contracts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-amber-600" /> Marketing &amp; Publicité
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gérez vos contrats publicitaires avec influenceurs, panneaux, radios, journaux…</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="create-contract-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau contrat
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-500"><Megaphone className="w-3.5 h-3.5" /> Contrats</div><div className="text-2xl font-bold text-[#1B3A4B] mt-1">{stats.total_contracts}</div><div className="text-xs text-gray-500">{stats.active_contracts} actifs · {stats.completed_contracts} terminés</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-500"><DollarSign className="w-3.5 h-3.5" /> Dépenses</div><div className="text-2xl font-bold text-amber-700 mt-1">{fmt(stats.total_spent)} <span className="text-xs">FCFA</span></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-500"><TrendingUp className="w-3.5 h-3.5" /> Vues</div><div className="text-2xl font-bold text-blue-700 mt-1">{fmt(stats.total_views_generated)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-500"><Target className="w-3.5 h-3.5" /> Leads générés</div><div className="text-2xl font-bold text-emerald-700 mt-1">{fmt(stats.total_leads_generated)}</div><div className="text-xs text-gray-500">/ {fmt(stats.target_leads)} cible</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-500"><Target className="w-3.5 h-3.5" /> Réalisation</div><div className="text-2xl font-bold text-[#1B3A4B] mt-1">{stats.lead_completion_pct}%</div><div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, stats.lead_completion_pct)}%` }} /></div></CardContent></Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {['all', 'draft', 'active', 'completed', 'cancelled'].map(k => (
          <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'}
                  onClick={() => setFilter(k)}
                  className={filter === k ? 'bg-[#1B3A4B] hover:bg-[#142D3A]' : ''}>
            {k === 'all' ? 'Tous' : (STATUS_STYLES[k]?.label || k)}
          </Button>
        ))}
        <span className="border-l mx-2" />
        {PARTNER_TYPES.map(pt => (
          <Button key={pt.value} size="sm" variant={filter === pt.value ? 'default' : 'outline'}
                  onClick={() => setFilter(pt.value)}
                  className={filter === pt.value ? 'bg-amber-600 hover:bg-amber-700' : ''}>
            <pt.icon className="w-3.5 h-3.5 mr-1" /> {pt.label}
          </Button>
        ))}
      </div>

      {/* Empty */}
      {filtered.length === 0 && (
        <Card><CardContent className="py-16 text-center">
          <Megaphone className="w-14 h-14 mx-auto text-amber-300 mb-3" />
          <p className="text-gray-500 mb-4">Aucun contrat publicitaire.</p>
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Créer le premier contrat
          </Button>
        </CardContent></Card>
      )}

      {/* List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((c) => {
          const Icon = typeIcon(c.partner_type);
          const st = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
          return (
            <Card key={c.id} className="border-2 hover:border-amber-300 transition-colors" data-testid={`contract-card-${c.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-amber-700 uppercase tracking-wider">{c.contract_number}</div>
                    <h3 className="text-lg font-bold text-[#1B3A4B] flex items-center gap-2 truncate">
                      <Icon className="w-4 h-4 text-amber-600" /> {c.partner_name}
                    </h3>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {PARTNER_TYPES.find(x => x.value === c.partner_type)?.label}
                      {c.partner_phone ? ` · ${c.partner_phone}` : ''}
                    </div>
                  </div>
                  <Badge className={`border ${st.className}`}>{st.label}</Badge>
                </div>

                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase">Début</div>
                    <div className="font-semibold text-[#1B3A4B] flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-amber-600" />{fmtDate(c.period_start)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase">Fin</div>
                    <div className="font-semibold text-[#1B3A4B] flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-amber-600" />{fmtDate(c.period_end)}</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 border border-amber-100">
                    <div className="text-[10px] font-semibold text-amber-800 uppercase">Montant</div>
                    <div className="font-bold text-amber-800">{fmt(c.total_amount)} FCFA</div>
                  </div>
                </div>

                {c.description && <p className="text-xs text-gray-600 line-clamp-2">{c.description}</p>}

                {c.payment_type === 'scheduled' && (c.payment_schedule || []).length > 0 && (
                  <div className="border-t pt-2">
                    <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">Échéances</div>
                    <div className="space-y-1">
                      {c.payment_schedule.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span>{s.label} · {fmt(s.amount)} FCFA · {fmtDate(s.due_date)}</span>
                          {s.paid_at ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100">Payé</Badge>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => markPaid(c, i)}
                                    disabled={busyId === c.id}
                                    className="h-6 text-[11px] px-2">Marquer payé</Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" onClick={() => doDownload(c)} disabled={busyId === c.id}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid={`pdf-${c.id}`}>
                    {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                    PDF
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => setConfirmDelete(c)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create/Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && cancelEdit()}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="contract-form-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-amber-600" />
              {editing?.id ? 'Modifier le contrat' : 'Nouveau contrat publicitaire'}
            </DialogTitle>
            <DialogDescription>Renseignez les informations du partenariat.</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom du partenaire *</Label>
                  <Input value={editing.partner_name} onChange={(e) => setEditing({ ...editing, partner_name: e.target.value })} placeholder="Ex : Fatoumata KEITA" data-testid="partner-name" />
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={editing.partner_type} onValueChange={(v) => setEditing({ ...editing, partner_type: v })}>
                    <SelectTrigger data-testid="partner-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PARTNER_TYPES.map(pt => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={editing.partner_phone} onChange={(e) => setEditing({ ...editing, partner_phone: e.target.value })} placeholder="+223 ..." />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={editing.partner_email} onChange={(e) => setEditing({ ...editing, partner_email: e.target.value })} />
                </div>
                <div>
                  <Label>Début du contrat *</Label>
                  <Input type="date" value={editing.period_start?.slice(0, 10) || ''} onChange={(e) => setEditing({ ...editing, period_start: e.target.value })} data-testid="period-start" />
                </div>
                <div>
                  <Label>Fin du contrat *</Label>
                  <Input type="date" value={editing.period_end?.slice(0, 10) || ''} onChange={(e) => setEditing({ ...editing, period_end: e.target.value })} data-testid="period-end" />
                </div>
              </div>

              <div>
                <Label>Description de la prestation</Label>
                <Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ex : 3 posts Instagram + 5 stories mettant en avant les biens neufs" />
              </div>

              {/* Deliverables */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Livrables ({editing.deliverables.length})</Label>
                  <Button size="sm" variant="outline" onClick={addDeliverable}><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>
                </div>
                <div className="mt-2 space-y-2">
                  {editing.deliverables.map((d, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 p-2 border rounded-lg">
                      <Input className="col-span-4" placeholder="Plateforme (Instagram, Radio…)" value={d.platform} onChange={(e) => updateDeliverable(i, { platform: e.target.value })} />
                      <Input className="col-span-4" placeholder="Prestation" value={d.label} onChange={(e) => updateDeliverable(i, { label: e.target.value })} />
                      <Input className="col-span-1" type="number" min="1" placeholder="Qté" value={d.quantity} onChange={(e) => updateDeliverable(i, { quantity: parseInt(e.target.value) || 1 })} />
                      <Input className="col-span-2" type="number" placeholder="Portée" value={d.expected_reach || ''} onChange={(e) => updateDeliverable(i, { expected_reach: parseInt(e.target.value) || null })} />
                      <Button size="sm" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeDeliverable(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label>Cible : Vues</Label><Input type="number" min="0" value={editing.kpis.target_views} onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, target_views: e.target.value } })} /></div>
                <div><Label>Cible : Clics</Label><Input type="number" min="0" value={editing.kpis.target_clicks} onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, target_clicks: e.target.value } })} /></div>
                <div><Label>Cible : Leads</Label><Input type="number" min="0" value={editing.kpis.target_leads} onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, target_leads: e.target.value } })} /></div>
                <div><Label>Réel : Vues</Label><Input type="number" min="0" value={editing.kpis.actual_views} onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, actual_views: e.target.value } })} /></div>
                <div><Label>Réel : Clics</Label><Input type="number" min="0" value={editing.kpis.actual_clicks} onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, actual_clicks: e.target.value } })} /></div>
                <div><Label>Réel : Leads</Label><Input type="number" min="0" value={editing.kpis.actual_leads} onChange={(e) => setEditing({ ...editing, kpis: { ...editing.kpis, actual_leads: e.target.value } })} /></div>
              </div>

              {/* Payment */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Type de paiement</Label>
                    <Select value={editing.payment_type} onValueChange={(v) => setEditing({ ...editing, payment_type: v })}>
                      <SelectTrigger data-testid="payment-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Forfait fixe</SelectItem>
                        <SelectItem value="scheduled">Échelonné (acomptes)</SelectItem>
                        <SelectItem value="performance">Commission au résultat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Montant total (FCFA)</Label>
                    <Input type="number" min="0" value={editing.total_amount} onChange={(e) => setEditing({ ...editing, total_amount: e.target.value })} data-testid="total-amount" />
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_STYLES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {editing.payment_type === 'performance' && (
                  <div className="mt-3 max-w-xs">
                    <Label>Commission (%)</Label>
                    <Input type="number" min="0" step="0.5" value={editing.commission_rate} onChange={(e) => setEditing({ ...editing, commission_rate: e.target.value })} />
                  </div>
                )}

                {editing.payment_type === 'scheduled' && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between">
                      <Label>Échéances de paiement ({editing.payment_schedule.length})</Label>
                      <Button size="sm" variant="outline" onClick={addInstallment}><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>
                    </div>
                    <div className="mt-2 space-y-2">
                      {editing.payment_schedule.map((s, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 p-2 border rounded-lg">
                          <Input className="col-span-4" placeholder="Ex: Acompte 30%" value={s.label} onChange={(e) => updateInstallment(i, { label: e.target.value })} />
                          <Input className="col-span-4" type="number" placeholder="Montant" value={s.amount} onChange={(e) => updateInstallment(i, { amount: e.target.value })} />
                          <Input className="col-span-3" type="date" value={s.due_date?.slice(0, 10) || ''} onChange={(e) => updateInstallment(i, { due_date: e.target.value })} />
                          <Button size="sm" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeInstallment(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit} disabled={saving}><X className="w-4 h-4 mr-1" /> Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-contract-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer ce contrat ?</DialogTitle>
            <DialogDescription>Le contrat <b>{confirmDelete?.contract_number}</b> ({confirmDelete?.partner_name}) sera supprimé définitivement.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doDelete} disabled={busyId === confirmDelete?.id}>
              {busyId === confirmDelete?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingContracts;
